import React from 'react';
import { Sun, ShieldCheck, Mail, Phone, MapPin, Navigation, MessageSquare } from 'lucide-react';
import { OfficeLocationMap } from './OfficeLocationMap';

export const Footer: React.FC = () => {
  return (
    <footer className="bg-slate-950 text-slate-400 border-t border-slate-900 pt-16 pb-12">
      <div className="w-full max-w-none px-4 md:px-8 lg:px-12 space-y-12">
        {/* Office Location Interactive Google Map (Centered on 5.5320, 7.4860) */}
        <OfficeLocationMap />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 pt-6 border-t border-slate-900">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-emerald-600 via-emerald-500 to-teal-400 flex items-center justify-center text-white shadow-lg shadow-emerald-500/20">
                <Sun className="w-6 h-6 animate-spin-slow" />
              </div>
              <div className="flex flex-col">
                <span className="font-black text-sm text-white tracking-tight uppercase leading-none">
                  Access Technologies
                </span>
                <span className="text-[10px] font-bold text-emerald-400 tracking-[0.14em] uppercase leading-none mt-1">
                  &amp; Renewable Energy
                </span>
              </div>
            </div>
            <p className="text-xs leading-relaxed text-slate-400">
              Leading the renewable energy transition across Nigeria. Delivering turnkey 1.5kVA to 20kVA+ solar systems with Grade-A LiFePO4 storage and 24/7 expert maintenance.
            </p>
            <div className="pt-2 text-xs text-slate-300 space-y-2">
              <div>
                <span className="font-bold text-white block">Headquarters:</span>
                <span>2 Abiriba St, Uzuokoli Road, Umuahia, 440233, Abia State</span>
              </div>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=2%20Abiriba%20St%2C%20Uzuokoli%20Road%2C%20Umuahia"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-[11px] uppercase tracking-wider transition-all shadow-md shadow-emerald-500/10"
              >
                <Navigation className="w-3.5 h-3.5 fill-current" />
                Get Directions
              </a>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-bold text-white text-sm">Engineered Packages</h4>
            <ul className="space-y-2 text-xs">
              <li><span className="hover:text-emerald-400 transition-colors">1.5kVA Essential Starter (₦1,250,000)</span></li>
              <li><span className="hover:text-emerald-400 transition-colors">3.5kVA Smart Home Standard (₦2,850,000)</span></li>
              <li><span className="hover:text-emerald-400 transition-colors">7.5kVA Executive Powerhouse (₦6,450,000)</span></li>
              <li><span className="hover:text-emerald-400 transition-colors">10kVA Commercial Solar Hub (₦9,800,000)</span></li>
            </ul>
          </div>

          <div className="space-y-3">
            <h4 className="font-bold text-white text-sm">Nigerian Offices & Hubs</h4>
            <ul className="space-y-2 text-xs">
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <strong className="text-white block">Headquarters (Umuahia):</strong>
                  <span className="block">2 Abiriba St, Uzuokoli Road, Umuahia, 440233</span>
                  <a
                    href="https://www.google.com/maps/dir/?api=1&destination=2%20Abiriba%20St%2C%20Uzuokoli%20Road%2C%20Umuahia"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-300 hover:text-white transition-colors"
                  >
                    <Navigation className="w-3 h-3 fill-current" /> Get Directions
                  </a>
                </div>
              </li>
              <li className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-emerald-400 shrink-0" /> Lekki Phase 1, Lagos State
              </li>
              <li className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-emerald-400 shrink-0" /> Wuse II, Abuja (FCT)
              </li>
              <li className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-emerald-400 shrink-0" /> GRA Phase 2, Port Harcourt
              </li>
            </ul>
          </div>

          <div className="space-y-3">
            <h4 className="font-bold text-white text-sm">Direct Contact & Support</h4>
            <ul className="space-y-2 text-xs">
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-emerald-400 shrink-0" />
                <a href="tel:+2348064752074" className="hover:text-emerald-400 transition-colors font-bold text-white">
                  Phone: 0806 475 2074
                </a>
              </li>
              <li className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-emerald-400 shrink-0" />
                <a href="https://wa.me/2348064752074" target="_blank" rel="noreferrer" className="hover:text-emerald-400 transition-colors">
                  WhatsApp: 0806 475 2074
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-emerald-400 shrink-0" /> support@accesstechnologiesandrenewableenergy.online
              </li>
              <li className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" /> COREN Registered & Licensed
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-slate-900 flex flex-col sm:flex-row justify-between items-center text-xs gap-4">
          <p>© {new Date().getFullYear()} Access Technologies and Renewable Energy Ltd. Address: 2 Abiriba St, Uzuokoli Road, Umuahia, 440233, Abia State. Phone: 0806 475 2074.</p>
          <div className="flex gap-6">
            <span className="hover:text-white transition-colors cursor-pointer">Privacy Policy</span>
            <span className="hover:text-white transition-colors cursor-pointer">Terms of Service</span>
            <span className="hover:text-white transition-colors cursor-pointer">Warranty Registration</span>
          </div>
        </div>

        <div className="pt-4 border-t border-slate-900/60 flex justify-center items-center text-xs">
          <span className="text-slate-500">
            Built By{' '}
            <a
              href="https://accesstechnologiesandrenewableenergy.online"
              target="_blank"
              rel="noreferrer"
              className="text-emerald-400 hover:text-emerald-300 font-bold transition-colors hover:underline"
            >
              GridNautic Technologies
            </a>
          </span>
        </div>
      </div>
    </footer>
  );
};

